/**
 * Product grid color swatches.
 *
 * Swaps the product card image to the selected colour's variant image in place.
 *
 * Covers both swatch markups this theme can render:
 *   - snippets/product-grid-add.liquid  .js-collection-swatch  (a <label> driving
 *     a variant radio, shown when settings.quickadd is on). Its click must keep
 *     its default behaviour so the radio and Add to Cart still work. This
 *     replaces the instant srcset overwrite that used to live in theme.js.
 *   - snippets/product-listing-swatches.liquid  .js-grid-swatch  (a link to the
 *     variant, shown when settings.show_collection_swatches is on). Its
 *     navigation is suppressed, which is what makes the swap happen in place.
 *
 * The new image is preloaded and decoded on a layer stacked over the card image,
 * then slid and faded in, so the swap never shows a half loaded image. The layer
 * stays in place afterwards and is only removed once its replacement has fully
 * faded in, which keeps rapid clicking from flashing back to the default colour.
 *
 * Hovering a swapped card peeks at the product's own first image: the card image
 * underneath is never overwritten, so the settled layer just fades out. Useful
 * when the variant image is a fabric close-up rather than a full shot.
 *
 * When the swatch instead carries a second shot of the same colour, that is
 * nested in the layer and shown on hover in place of the peek, taking over the
 * theme's secondary image hover, which belongs to the default colour. It is
 * fetched only after the swap has settled, to keep it off the critical path.
 *
 * Swatches without a variant image keep their default link behaviour, as does a
 * no-JS visitor.
 */
(function () {
  "use strict";

  var selectors = {
    swatch: '.js-grid-swatch[data-variant-image], .js-collection-swatch[data-href]',
    listing: '.js-product-listing',
    imageWrapper: '.prod-image',
    ratioBox: '.prod-image .reveal > .box-ratio',
    swatchList: '.color-swatch .options'
  };

  var classes = {
    selected: 'is-selected',
    active: 'swatch-image-active',
    layer: 'grid-swatch__layer',
    layerIn: 'is-in',
    layerSettled: 'is-settled',
    layerDismissed: 'is-dismissed',
    hasHover: 'has-hover-image',
    hover: 'grid-swatch__layer-hover'
  };

  var DISMISS_MS = 350;

  // Mirrors the widths used by snippets/basic-responsive-image.liquid.
  var fallbackWidths = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800, 2000];

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * @description Resolve a protocol relative CDN url into a URL object.
   * @param  {String} url Image url.
   * @return {URL} Absolute url.
   */
  function _absoluteUrl(url) {
    return new URL(url, window.location.href);
  }

  /**
   * @description Build an image url at a given width.
   * @param  {String} url Image url.
   * @param  {Number} width Requested width.
   * @return {String} Image url at the requested width.
   */
  function _urlAtWidth(url, width) {
    var parsed = _absoluteUrl(url);
    parsed.searchParams.set('width', String(width));
    return parsed.href;
  }

  /**
   * @description Read the widths already used by the card image so the swapped
   * image keeps the exact same responsive behaviour.
   * @param  {String} srcset Existing srcset attribute.
   * @return {Array} List of widths.
   */
  function _widthsFromSrcset(srcset) {
    if (!srcset) {
      return fallbackWidths;
    }

    var widths = srcset.split(',').reduce(function (collected, candidate) {
      var match = candidate.trim().match(/\s(\d+)w$/);

      if (match) {
        collected.push(parseInt(match[1], 10));
      }

      return collected;
    }, []);

    return widths.length ? widths : fallbackWidths;
  }

  /**
   * @description Build a srcset for the variant image.
   * @param  {String} url Image url.
   * @param  {Array} widths List of widths.
   * @return {String} srcset attribute value.
   */
  function _buildSrcset(url, widths) {
    return widths.map(function (width) {
      return _urlAtWidth(url, width) + ' ' + width + 'w';
    }).join(', ');
  }

  /**
   * @description Resolve once the image is downloaded and decoded, reject if it
   * fails so the old image is left untouched.
   * @param  {HTMLImageElement} image Image element.
   * @return {Promise} Resolves when the image is paintable.
   */
  function _whenPaintable(image) {
    return new Promise(function (resolve, reject) {
      if (image.complete) {
        if (image.naturalWidth) {
          resolve();
        } else {
          reject();
        }
        return;
      }

      image.addEventListener('load', function () { resolve(); }, { once: true });
      image.addEventListener('error', function () { reject(); }, { once: true });
    }).then(function () {

      // Decoding up front avoids a stutter on the first animated frame.
      return image.decode ? image.decode().catch(function () {}) : undefined;
    });
  }

  /**
   * @description Mark the clicked swatch as the selected one.
   * @param  {HTMLElement} swatch Clicked swatch link.
   */
  function _setSelected(swatch) {
    var item = swatch.closest('li');
    var list = swatch.closest(selectors.swatchList);

    if (!item || !list) {
      return;
    }

    list.querySelectorAll('li').forEach(function (sibling) {
      sibling.classList.remove(classes.selected);
    });

    item.classList.add(classes.selected);
  }

  /**
   * @description Build an image box mirroring the markup of
   * snippets/basic-responsive-image.liquid, so the theme's own image rules keep
   * applying to it.
   * @param  {String} sizes sizes attribute copied from the card image.
   * @param  {String} alt Alt text.
   * @return {HTMLElement} A .box-ratio wrapping an img.
   */
  function _buildImageBox(sizes, alt) {
    var box = document.createElement('div');
    var image = document.createElement('img');

    box.className = 'box-ratio';

    image.setAttribute('alt', alt || '');
    image.setAttribute('sizes', sizes || '');
    image.setAttribute('draggable', 'false');

    box.appendChild(image);

    return box;
  }

  /**
   * @description Build the layer holding the incoming variant image, plus the
   * hover shot of the same colour when the swatch carries one.
   * @param  {HTMLImageElement} baseImage The card image being covered.
   * @param  {Object} variant Urls and alt text for the selected colour.
   * @return {HTMLElement} The layer element, not yet in the document.
   */
  function _buildLayer(baseImage, variant) {
    var layer = document.createElement('div');
    var sizes = baseImage.getAttribute('sizes');
    var box = _buildImageBox(sizes, variant.alt || baseImage.getAttribute('alt'));
    var image = box.querySelector('img');

    layer.className = classes.layer;

    image.setAttribute('srcset', variant.srcset);
    image.setAttribute('src', variant.src);

    layer.appendChild(box);

    if (variant.hoverSrc) {
      var hover = document.createElement('div');
      var hoverBox = _buildImageBox(sizes, '');
      var hoverImage = hoverBox.querySelector('img');

      // Tells the stylesheet to show this shot on hover rather than fading the
      // layer out to peek at the product's first image.
      layer.classList.add(classes.hasHover);
      hover.className = classes.hover;

      // Held back until the swap has settled, see _loadHoverImage.
      hoverImage.setAttribute('data-srcset', variant.hoverSrcset);
      hoverImage.setAttribute('data-src', variant.hoverSrc);

      hover.appendChild(hoverBox);
      layer.appendChild(hover);
    }

    return layer;
  }

  /**
   * @description Start fetching the hover shot, once the swap is out of the way.
   * @param  {HTMLElement} layer Layer holding the hover image.
   */
  function _loadHoverImage(layer) {
    var image = layer.querySelector('.' + classes.hover + ' img[data-src]');

    if (!image) {
      return;
    }

    image.setAttribute('srcset', image.getAttribute('data-srcset'));
    image.setAttribute('src', image.getAttribute('data-src'));
    image.removeAttribute('data-srcset');
    image.removeAttribute('data-src');
  }

  /**
   * @description Drop every layer except the one now on top.
   * @param  {HTMLElement} reveal Image wrapper.
   * @param  {HTMLElement} keep Layer to keep.
   */
  function _dropStaleLayers(reveal, keep) {
    reveal.querySelectorAll('.' + classes.layer).forEach(function (layer) {
      if (layer !== keep) {
        layer.remove();
      }
    });
  }

  /**
   * @description Fade and slide the layer into view, then clean up underneath.
   * @param  {HTMLElement} reveal Image wrapper.
   * @param  {HTMLElement} layer Layer to reveal.
   */
  function _revealLayer(reveal, layer) {

    if (reducedMotion.matches) {
      layer.classList.add(classes.layerIn);
      layer.classList.add(classes.layerSettled);
      _dropStaleLayers(reveal, layer);
      _loadHoverImage(layer);
      return;
    }

    var duration = parseFloat(window.getComputedStyle(layer).transitionDuration) * 1000;
    var settled = false;

    function _settle() {
      if (settled) {
        return;
      }

      settled = true;
      layer.removeEventListener('transitionend', _onTransitionEnd);

      // Marks the swap as done. Gates the hover peek in CSS, and only then is it
      // safe to shorten the layer's transition, which the peek does.
      layer.classList.add(classes.layerSettled);

      _dropStaleLayers(reveal, layer);
      _loadHoverImage(layer);
    }

    function _onTransitionEnd(event) {
      if (event.target === layer && event.propertyName === 'opacity') {
        _settle();
      }
    }

    layer.addEventListener('transitionend', _onTransitionEnd);

    // Safety net, so a transition that never fires cannot leave layers stacked up.
    window.setTimeout(_settle, (duration || 450) + 200);

    // Flush the initial state so the transition runs from it.
    void layer.offsetWidth;
    layer.classList.add(classes.layerIn);
  }

  /**
   * @description Handle click event.
   * @typedef {object} MouseEvent
   * @param  {MouseEvent} event Event object.
   */
  function _handleClick(event) {

    // Bail if no Event object.
    if (!event || !event.target || !event.target.closest) {
      return;
    }

    var swatch = event.target.closest(selectors.swatch);

    // Bail if not a grid color swatch.
    if (!swatch) {
      return;
    }

    var listing = swatch.closest(selectors.listing);
    var ratioBox = listing ? listing.querySelector(selectors.ratioBox) : null;
    var baseImage = ratioBox ? ratioBox.querySelector('img') : null;

    // Bail without preventing the click, so a link swatch still navigates.
    if (!baseImage) {
      return;
    }

    // Only the link swatch needs suppressing. The label swatch must keep its
    // default action, which is what checks the variant radio behind it.
    if (swatch.tagName === 'A') {
      event.preventDefault();
    }

    var reveal = ratioBox.parentNode;
    var widths = _widthsFromSrcset(baseImage.getAttribute('srcset'));
    var variantImage = swatch.getAttribute('data-variant-image') || swatch.getAttribute('data-href');
    var hoverImage = swatch.getAttribute('data-variant-hover-image');

    var variant = {
      src: _urlAtWidth(variantImage, 1200),
      srcset: _buildSrcset(variantImage, widths),
      alt: swatch.getAttribute('data-variant-alt'),
      hoverSrc: hoverImage ? _urlAtWidth(hoverImage, 1200) : null,
      hoverSrcset: hoverImage ? _buildSrcset(hoverImage, widths) : null
    };

    var layers = reveal.querySelectorAll('.' + classes.layer + ' > .box-ratio > img');
    var current = layers.length ? layers[layers.length - 1] : baseImage;

    _setSelected(swatch);

    // Already showing this colour.
    if (current.getAttribute('src') === variant.src) {
      return;
    }

    var layer = _buildLayer(baseImage, variant);

    // Suppress the secondary image hover, it belongs to the default colour.
    listing.querySelector(selectors.imageWrapper)?.classList.add(classes.active);

    // Appended while still transparent, so the download starts right away
    // without the visitor seeing a partially loaded image.
    reveal.appendChild(layer);

    _whenPaintable(layer.querySelector('.box-ratio > img')).then(function () {

      // A later click, or the slider taking the image back, already won the race.
      if (!layer.isConnected ||
          layer !== reveal.lastElementChild ||
          layer.classList.contains(classes.layerDismissed)) {
        layer.remove();
        return;
      }

      _revealLayer(reveal, layer);
    }).catch(function () {
      layer.remove();
    });
  }

  /**
   * @description Fade the colour layers off a card and drop them, handing the
   * card image back to whatever sits underneath. Called by the image slider when
   * the visitor starts browsing images, since the layer would cover the slides.
   * The swatch stays selected, so Add to Cart still uses the chosen variant.
   * @param  {HTMLElement} listing A .js-product-listing element.
   */
  function dismiss(listing) {
    if (!listing) {
      return;
    }

    var wrapper = listing.querySelector(selectors.imageWrapper);
    var layers = listing.querySelectorAll('.' + classes.layer);

    if (!layers.length) {
      return;
    }

    layers.forEach(function (layer) {
      if (layer.classList.contains(classes.layerDismissed)) {
        return;
      }

      layer.classList.add(classes.layerDismissed);
      window.setTimeout(function () {
        layer.remove();
      }, DISMISS_MS);
    });

    // No colour layer left, so the theme's own hover may run again.
    if (wrapper) {
      wrapper.classList.remove(classes.active);
    }
  }

  document.addEventListener('click', _handleClick);

  window.WAU = window.WAU || {};
  window.WAU.GridSwatchImage = { dismiss: dismiss };
})();
